import './DynamicColumnsA.scss';

import { b2x } from '@b2x/react/src';
import classNames from 'classnames';

import { Container } from '../Container';
import { ProductTile } from '../ProductTile';

interface CardComponentProps {
  asset?: b2x.contentTypes.AssetContentSectionV2;
  cta?: b2x.contentTypes.CtaContentSection;
  title?: string;
}

interface DynamicColumnsSectionContentType {
  columns?: b2x.contentTypes.ContentSectionList<{
    component?: b2x.contentTypes.ContentSectionSwitch<{
      asset?: b2x.contentTypes.AssetContentSectionV2;
      card?: CardComponentProps;
      product?: {
        id?: string;
      };
    }>;
    size?: b2x.ColSize;
  }>;
  cta?: b2x.contentTypes.CtaContentSection;
  title?: string;
  titleColor?: string;
}

export interface DynamicColumnsAContentType {
  background?: string;
  sections?: b2x.contentTypes.ContentSectionList<DynamicColumnsSectionContentType>;
}

const CardComponent = ({ asset, cta, title }: CardComponentProps) => {
  return (
    <b2x.Div className="card-component" display="grid">
      <div className="position-relative" style={{ gridColumn: 1, gridRow: 1 }}>
        <b2x.Div className="asset-container">
          <b2x.AssetV2 {...asset} fluid />
        </b2x.Div>
      </div>
      <b2x.Div
        alignItems={'center'}
        className="text-white copy-container"
        display="flex"
        flexDirection="column"
        gap={3}
        justifyContent={'center'}
        style={{ gridColumn: 1, gridRow: 1 }}
      >
        {title && (
          <b2x.H4 className="h1" fontWeight={700}>
            {title}
          </b2x.H4>
        )}
        {cta && <b2x.CtaFromContent {...cta} />}
      </b2x.Div>
    </b2x.Div>
  );
};

const MultiColumnSection = ({ columns, cta, title, titleColor }: DynamicColumnsSectionContentType) => {
  return (
    <b2x.Div display={'flex'} flexDirection={'column'} gap={{ md: 4, xs: 3 }}>
      {title && (
        <b2x.H3
          className={classNames('section-title small letter-spacing-md', {
            [`text-${titleColor}`]: !!titleColor,
          })}
          fontWeight={700}
          margin={0}
          textAlign={'center'}
        >
          {b2x.formatHtml(title)}
        </b2x.H3>
      )}
      <b2x.EqualHeight>
        <b2x.Row gap={4}>
          {columns?.map((column) => {
            const component = column.component?.at(0);
            if (!component) {
              return null;
            }

            return (
              <b2x.Col
                className="dynamic-columns-a-column"
                key={column.contentSectionId}
                size={{
                  lg: column.size,
                  xs: 12,
                }}
              >
                {component.product?.id ? (
                  <b2x.ProductById id={component.product.id}>
                    {(product) =>
                      product !== undefined && (
                        <b2x.Listing name={`product-${component.product?.id}`} products={[product]}>
                          <ProductTile product={product} />
                        </b2x.Listing>
                      )
                    }
                  </b2x.ProductById>
                ) : component.asset ? (
                  <b2x.AssetV2 {...component.asset} />
                ) : component.card ? (
                  <CardComponent {...component.card} />
                ) : null}
              </b2x.Col>
            );
          })}
        </b2x.Row>
      </b2x.EqualHeight>
      <b2x.Div className="section-cta" textAlign="center">
        {cta?.label && <b2x.CtaFromContent {...cta} />}
      </b2x.Div>
    </b2x.Div>
  );
};

export const DynamicColumnsA = ({ background, sections }: DynamicColumnsAContentType) => {
  if (!sections?.length) {
    return null;
  }

  return (
    <div className="dynamic-columns-a">
      <Container className="dynamic-columns-a-container" fluid="always">
        <b2x.Div
          background={background}
          className="sections-container"
          padding={{ md: !!background ? 4 : 0, xs: !!background ? 3 : 0 }}
        >
          <Container>
            <b2x.Div display="flex" flexDirection="column" gap={4}>
              {sections.map((section) => (
                <MultiColumnSection {...section} key={section.contentSectionId} />
              ))}
            </b2x.Div>
          </Container>
        </b2x.Div>
      </Container>
    </div>
  );
};
